// context/ToastContext.js
// Global toast notification system.
// Toasts auto-dismiss after 3 seconds (configurable) and have an X close button.
// Usage: const { addToast } = useToast();
//        addToast('Title', 'Message body', 'success');

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

let _nextId = 1;

const TYPE_META = {
  info:     { bg: '#1e293b', accent: '#3b82f6', icon: 'ℹ️'  },
  success:  { bg: '#14532d', accent: '#22c55e', icon: '✅'  },
  warning:  { bg: '#78350f', accent: '#f59e0b', icon: '⚠️'  },
  error:    { bg: '#7f1d1d', accent: '#ef4444', icon: '🚨'  },
  task:     { bg: '#1e1b4b', accent: '#818cf8', icon: '📋'  },
  session:  { bg: '#0c4a6e', accent: '#38bdf8', icon: '🏃'  },
  reminder: { bg: '#4a1d96', accent: '#a78bfa', icon: '🔔'  },
  renewal:  { bg: '#7c2d12', accent: '#fb923c', icon: '🔄'  },
  inactive: { bg: '#1c1917', accent: '#a3a3a3', icon: '💤'  },
  payment:  { bg: '#052e16', accent: '#4ade80', icon: '💳'  },
};

function ToastItem({ toast, onRemove }) {
  const meta = TYPE_META[toast.type] || TYPE_META.info;
  return (
    <div
      onClick={() => { if (toast.onClick) { toast.onClick(); onRemove(toast.id); } }}
      style={{
        display: 'flex', alignItems: 'flex-start',
        background: meta.bg,
        borderLeft: '3px solid ' + meta.accent,
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
        minWidth: 260, maxWidth: 340,
        pointerEvents: 'auto',
        cursor: toast.onClick ? 'pointer' : 'default',
        animation: 'srm_slideIn 0.22s ease-out forwards',
      }}>
      <span style={{ fontSize: 17, flexShrink: 0, marginRight: 9, marginTop: 1 }}>{meta.icon}</span>
      <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
        {toast.title && (
          <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {toast.title}
          </div>
        )}
        {toast.message && (
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.45,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden' }}>
            {toast.message}
          </div>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); onRemove(toast.id); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#94a3b8', fontSize: 18, padding: 0, flexShrink: 0,
          lineHeight: 1, marginTop: -1,
        }}
      >×</button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  /**
   * addToast(title, message, type, duration)
   * type: 'info' | 'success' | 'warning' | 'error' | 'task' | 'session' | 'reminder' | 'renewal' | 'inactive' | 'payment'
   * duration: ms before auto-dismiss (default 3000)
   */
  const addToast = useCallback((title, message, type = 'info', duration = 3000, onClick) => {
    const id = _nextId++;
    setToasts(prev => [...prev, { id, title, message, type, onClick }]);
    timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <style>{`
        @keyframes srm_slideIn {
          from { opacity: 0; transform: translateX(50px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {/* Toast stack — bottom-right, column-reverse so newest is at bottom */}
      <div style={{
        position: 'fixed', bottom: 24, right: 20,
        display: 'flex', flexDirection: 'column',
        zIndex: 99999, pointerEvents: 'none',
      }}>
        {toasts.map((toast, i) => (
          <div key={toast.id} style={{ marginTop: i === 0 ? 0 : 8 }}>
            <ToastItem toast={toast} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
